import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { ContactItem, useContactQuery, useDeleteContacts } from 'src/services/contact';
import { Partner } from 'src/services/partner';
import { useNavigate } from 'react-router-dom';
import { useDataViewController } from '../hooks/useDataViewController';
import { DataView } from '../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { SearchModels } from '../components/ui/TableOverview/utils/SearchFieldRegistry';
import { OverlayNavProvider } from '../providers/OverlayNavProvider';

const columnHelper = createColumnHelper<ContactItem>();

function createColumn(key: string, header: string, size?: number, className?: string, id?: string) {
  return columnHelper.accessor(key as any, {
    id: id ?? key,
    size,
    meta: {
      label: header,
    },
    header: ({ column }) => (
      <div className="flex items-center gap-x-12">
        <span className="typo-text-12 text-faded"> {column.columnDef.meta?.label} </span>
      </div>
    ),
    cell: info => (
      <div className="flex items-center gap-x-12">
        <div className="flex flex-col gap-y-4">
          <span className={className ?? 'typo-text-14 text-full'}> {info.getValue()} </span>
        </div>
      </div>
    ),
  });
}

export const CONTACTS_SETTINGS_PATH = '/settings/contacts';

type ContactsProps = {
  basePath?: string;
};

function Contacts({ basePath = '/contact' }: ContactsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: deleteContacts } = useDeleteContacts();

  const labels = {
    id: t('general.id'),
    name: t('general.name'),
    company_name: t('general.company_name'),
    address_street: t('general.street'),
    address_zip_code: t('general.zip_code'),
    address_city: t('general.city'),
    address_country_name: t('general.country'),
    email: t('general.email'),
    address_street2: t('general.additional_street'),
  };
  const columns = [
    ...(
      [
        ['name', labels.name, 220, 'typo-text-14--bold'],
        ['company_name', labels.company_name, 200, 'typo-text-14 text-faded'],
        ['email', labels.email],
        ['address.street', labels.address_street, 200, 'typo-text-14 text-faded', 'street'],
        ['address.zip_code', labels.address_zip_code, 120, 'typo-text-14 text-faded', 'zip'],
        ['address.city', labels.address_city, 200, 'typo-text-14 text-faded', 'city'],
        ['address.country_name', labels.address_country_name, 200, 'typo-text-14 text-faded', 'country_id'],
        ['address.street2', labels.address_street2, 200, 'typo-text-14 text-faded', 'street2'],
      ] as const
    ).map(([key, header, size, className, id]) => createColumn(key, header, size, className, id)),
  ];

  const controller = useDataViewController();
  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);

  return (
    <OverlayNavProvider entityIds={entityIds} basePath={basePath}>
      <DataView<Partner>
        onDataLoaded={items => setEntityIds(items.map(i => i.id).filter((id): id is number => id != null))}
        filterModelId="res.partner"
        headline={t('navbar.contact')}
        emptyState={<NoDataPlaceholder headline={t('general.empty_contacts')} />}
        modelKey={SearchModels.Partner}
        controller={controller}
        withSaveFilters={false}
        actions={[
          {
            title: t('contact.create_contact'),
            icon: 'plus',
            onExecute: () => {
              navigate(`${basePath}/create`);
            },
            showInToolbar: true,
          },
          {
            title: t('contact.edit'),
            icon: 'edit',
            isRowClickAction: true,
            onExecute: selected => {
              if (selected.length > 0) {
                navigate(`${basePath}/${selected[0].id}`);
              }
            },
            showInContextMenu: true,
          },
          {
            title: t('general.delete_contact'),
            titlePlural: t('general.delete_contacts'),
            icon: 'close',
            showInContextMenu: true,
            bulkSupport: true,
            toast: {
              renderSuccessMessage: () => t('success.delete_contact_success'),
              renderErrorMessage: () => t('errors.delete_contact_failed'),
            },
            onExecute: async selected => {
              const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
              deleteContacts({
                params: {
                  path: {
                    model: 'res.partner',
                  },
                },
                body: ids,
              });
            },
            confirmSettings: {
              renderTitle: selected => {
                if (selected.length > 1) {
                  return t('general.delete_contacts');
                }
                return t('general.delete_contact');
              },
              renderContent: selected => {
                if (selected.length > 1) {
                  return t('general.delete_contacts_text');
                }
                return t('general.delete_contact_text');
              },
              confirmLabel: t('general.confirm'),
              cancelLabel: t('general.cancel'),
            },
          },
        ]}
        columns={columns}
        useDataQuery={useContactQuery}
      />
    </OverlayNavProvider>
  );
}

export default Contacts;
